import React from 'react';

import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { SelectHierarchyLabel } from '@weezevent/nacre';
import { translateCountry } from '../../pages/attendee/detail/components/utils';
import i18n from 'i18next';

function rateOption(tax) {
    const val = tax.tax_values.tax_supplemental_value ? tax.tax_values.tax_value + tax.tax_values.tax_supplemental_value : tax.tax_values.tax_value;

    return {
        key: tax.id,
        text: `${tax.label.replace('%1$s', `${val}%`)} - ${tax.country}`,
        value: tax.id,
        parent: tax.country,
        tax_value: val
    };
}

export default function SelectTaxes({
    options = null,
    taxes = null,
    onChange = () => {},
    value = null,
    label = 'Tax',
    placeholder = 'ticket.event.tax_placeholder',
    placeholderSearch = 'ticket.common.action.search',
    height = 'medium',
    ellipsis = false,
    errors = [],
    disabled = false,
    withOptionTaxEvent = false,
    eventCountry = null,
    ...rest
}) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const optionTaxEvent = {
        key: 'event',
        text: t('ticket.rates.infos.eventTax'),
        value: 'event'
    };
    const sortedTaxesByCountry = React.useMemo(() => {
        if (!taxes) {
            return [];
        }

        const enrichedTaxes = taxes.map(tax => ({
            ...tax.object,
            // Tax exemption gets a country name of '_' that makes it show at the top of the list
            _countryName: tax.country === 'DOM-TOM' ? 'DOM-TOM' : translateCountry('en-US', tax.country) || '_',
            _rateOption: tax.country !== '' && tax.id !== 0 ? rateOption(tax) : null
        }));

        return enrichedTaxes.sort((taxA, taxB) => {
            const nameSort = taxA._countryName.localeCompare(taxB._countryName);
            if (nameSort !== 0) return nameSort;

            if (taxA.country !== 'CA' && taxB.country !== 'CA') {
                const valA = taxA._rateOption?.tax_value ?? 0;
                const valB = taxB._rateOption?.tax_value ?? 0;
                return valA - valB;
            }
            return 0;
        });
    }, [taxes, translateCountry, rateOption]);

    const taxsOptions = React.useMemo(() => {
        if (options) {
            return options;
        }

        if (!sortedTaxesByCountry) {
            return [];
        }

        let _options = [];
        let firstTaxIndex = 0;
        if (withOptionTaxEvent) {
            // offset the event country tax rates down by one
            // to show below the "event tax rate" option.
            _options.push(optionTaxEvent);
            firstTaxIndex++;
        }

        sortedTaxesByCountry.forEach(tax => {
            if (tax.country === '' && tax.id === 0) {
                // offset the event country tax rates down by one
                // to show below the "tax exemption" option.
                firstTaxIndex += 1;
                _options.push({ key: tax.id, text: tax.label, value: tax.id, tax_value: tax.tax_values.tax_value });
                return;
            }
            //@Todo case particular optgroup, synchro optgroup in Pyvar
            if ([11, 12, 13, 14, 15].includes(tax.id)) {
                tax.country = 'DOM-TOM';
            }
            let groupIndex = _options.findIndex(opt => opt.value === tax.country);
            //Create group with child
            if (groupIndex === -1) {
                _options.push({
                    key: tax.country,
                    text: tax.country === 'DOM-TOM' ? tax.country : translateCountry(locale, tax.country),
                    value: tax.country,
                    children: [tax?._rateOption]
                });
                return;
            }
            //Put child in group if group already created
            _options[groupIndex].children.push(tax._rateOption);
        });
        // place taxes for the country of the event on the first place
        if (eventCountry) {
            // used this method to move object inside an array https://stackoverflow.com/a/6470794
            const taxToMoveIndex = _options.findIndex(opt => opt.value === eventCountry);
            if (taxToMoveIndex !== -1 && taxToMoveIndex !== firstTaxIndex) {
                const [taxToMove] = _options.splice(taxToMoveIndex, 1);
                _options.splice(firstTaxIndex, 0, taxToMove);
            }
        }
        return _options;
    }, [taxes, locale, rateOption, withOptionTaxEvent, optionTaxEvent, eventCountry]);

    if (!taxes && !options) {
        return null;
    }

    return (
        <SelectHierarchyLabel
            onChange={(optionValue, option) => onChange(optionValue, option)}
            options={taxsOptions}
            value={value}
            placeholder={t(placeholder)}
            placeholderSearch={t(placeholderSearch)}
            searchable={true}
            height={height}
            ellipsis={ellipsis}
            errors={errors}
            disabled={disabled}
            widthIndent="10px"
            label={label}
            {...rest}
        />
    );
}

SelectTaxes.propTypes = {
    taxes: PropTypes.array,
    options: PropTypes.array,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
    placeholder: PropTypes.string,
    placeholderSearch: PropTypes.string,
    height: PropTypes.oneOf(['mini', 'medium', 'maxi']),
    ellipsis: PropTypes.bool,
    errors: PropTypes.array,
    disabled: PropTypes.bool,
    withOptionTaxEvent: PropTypes.bool,
    eventCountry: PropTypes.string
};
